
import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Edit, Save, X } from "lucide-react";
import { useDocumentOperations } from "@/hooks/use-document-operations";
import { DocumentAIAssistant } from "./DocumentAIAssistant";
import { supabase } from "@/integrations/supabase/client";

interface DocumentContentEditorProps {
  section: string;
  content: string;
  documentId: string;
  field: string;
  onUpdate: (newContent: string) => void;
  showHint?: boolean;
}

export function DocumentContentEditor({ 
  section, 
  content, 
  documentId, 
  field, 
  onUpdate,
  showHint = false 
}: DocumentContentEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(content || "");
  const [hasAssessment, setHasAssessment] = useState(false);

  const {
    isLoading,
    error,
    progress,
    handleSave,
    handleAIAssist
  } = useDocumentOperations(documentId, field, onUpdate);

  useEffect(() => {
    setEditContent(content || "");
  }, [content]);

  useEffect(() => {
    checkAssessment();
  }, []);

  const checkAssessment = async () => {
    try {
      const { data } = await supabase
        .from('business_assessments')
        .select('id')
        .single();
      setHasAssessment(!!data);
    } catch (error) {
      setHasAssessment(false);
    }
  };

  const handleEditSave = async () => {
    const success = await handleSave(editContent);
    if (success) {
      setIsEditing(false);
    }
  };

  const handleEditCancel = () => {
    setEditContent(content || "");
    setIsEditing(false);
  };

  const handleAIGenerate = async () => {
    const generatedContent = await handleAIAssist();
    if (generatedContent) {
      setEditContent(generatedContent);
    }
  };

  const isEmpty = !content || content.trim() === '';

  if (isEditing) {
    return (
      <Card className="w-full">
        <CardContent className="p-4 space-y-4">
          <Textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            placeholder={`Enter ${section.toLowerCase()} content...`}
            className="min-h-[200px] resize-none"
            disabled={isLoading}
          />
          
          <div className="flex justify-between items-center">
            <DocumentAIAssistant
              isLoading={isLoading}
              onAIAssist={handleAIGenerate}
              progress={progress}
              hasAssessment={hasAssessment}
            />
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleEditCancel}
                disabled={isLoading}
              >
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleEditSave}
                disabled={isLoading || editContent.trim() === ''}
              >
                <Save className="w-4 h-4 mr-1" />
                Save
              </Button>
            </div>
          </div>
          
          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {isEmpty ? (
        <Card className="border-dashed border-2">
          <CardContent className="p-8 text-center">
            <div className="space-y-4">
              <p className="text-gray-500">
                No content yet for {section.toLowerCase()}
              </p>
              
              <div className="flex justify-center gap-3">
                <DocumentAIAssistant
                  isLoading={isLoading}
                  onAIAssist={handleAIGenerate}
                  progress={progress}
                  hasAssessment={hasAssessment}
                />
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  disabled={isLoading}
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Write Manually
                </Button>
              </div>
              
              {showHint && hasAssessment && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    💡 Use Smart AI Assist to generate content based on your business assessment!
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <div className="bg-white rounded-lg p-4 border">
            <div className="prose max-w-none">
              {content.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-2 last:mb-0">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
          
          <div className="flex justify-between items-center">
            <DocumentAIAssistant
              isLoading={isLoading}
              onAIAssist={handleAIGenerate}
              progress={progress}
              hasAssessment={hasAssessment}
            />
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
              disabled={isLoading}
            >
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </Button>
          </div>
        </div>
      )}
      
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
}
